import { faEllipsis, faEye, faPen, faPowerOff, faPrint } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useRef } from 'react';
import { PackingStation, type PackingStationRelationsTabKey } from '../../models/PackingStation';
import { useTogglePackingStationStatus } from '../../hooks/useTogglePackingStationStatus';

type LabelSize = 'small' | 'large';

interface DropdownActionsProps {
  station: PackingStation;
  onOpenDetail?: (station: PackingStation, tab?: PackingStationRelationsTabKey) => void;
  onOpenEdit?: (station: PackingStation) => void;
  onPrint?: (station: PackingStation, size: LabelSize) => void;
  refetch?: () => void;
}

export const DropdownActions = ({ station, onOpenDetail, onOpenEdit, onPrint, refetch }: DropdownActionsProps) => {
  const { t } = useTranslation('packingStationsModule');
  const { toggleStatus, isToggling } = useTogglePackingStationStatus();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const handleToggleConfirm = () => {
    toggleStatus(station.id, station.active, () => refetch?.());
  };

  // Diferimos la apertura del modal para evitar el conflicto conocido de
  // Radix DropdownMenu + Dialog (race de foco que puede reabrir el menú).
  const openConfirmDeactivate = () => {
    const isDeactivating = station.active;
    setTimeout(() => {
      confirmModalRef.current?.open(
        handleToggleConfirm,
        undefined,
        t(isDeactivating ? 'actions.confirm.deactivateTitle' : 'actions.confirm.activateTitle'),
        t(isDeactivating ? 'actions.confirm.deactivateMessage' : 'actions.confirm.activateMessage', {
          code: station.code,
        }),
        t(isDeactivating ? 'actions.deactivate' : 'actions.activate'),
      );
    }, 0);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="hover:bg-muted rounded-md p-2 transition-colors"
            data-testid="packing-station-dropdown-trigger"
            onClick={(e) => e.stopPropagation()}
          >
            <FontAwesomeIcon icon={faEllipsis} className="text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-48 border-card-border"
          data-testid="packing-station-dropdown-content"
        >
          {/* Ver detalle */}
          <DropdownMenuItem
            onSelect={() => {
              setTimeout(() => onOpenDetail?.(station), 0);
            }}
            data-testid="view-packing-station-detail"
          >
            <FontAwesomeIcon icon={faEye} className="mr-2 w-3.5 h-3.5" />
            {t('actions.viewDetail')}
          </DropdownMenuItem>

          {/* Editar código y estado (misma modal que creación) */}
          <DropdownMenuItem
            onSelect={() => {
              setTimeout(() => onOpenEdit?.(station), 0);
            }}
            data-testid="edit-packing-station"
          >
            <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5 h-3.5" />
            {t('actions.edit')}
          </DropdownMenuItem>

          {onPrint && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid={`dropdown-print-${station.id}`}>
                <FontAwesomeIcon icon={faPrint} className="mr-2 w-3.5 h-3.5" />
                {t('actions.print')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  data-testid={`dropdown-print-small-${station.id}`}
                  onClick={() => onPrint(station, 'small')}
                >
                  {t('label.size_small')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`dropdown-print-large-${station.id}`}
                  onClick={() => onPrint(station, 'large')}
                >
                  {t('label.size_large')}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {/* Activar / Desactivar */}
          <DropdownMenuItem
            onSelect={openConfirmDeactivate}
            disabled={isToggling}
            data-testid="toggle-packing-station-status"
          >
            <FontAwesomeIcon icon={faPowerOff} className="mr-2 w-3.5 h-3.5" />
            {station.active ? t('actions.deactivate') : t('actions.activate')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
